import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getUserInfo, verifyToken, fetchUserByToken } from '../../utils/api';

// Async thunk to fetch user info using stored JWT token
export const fetchUserInfo = createAsyncThunk(
    'auth/fetchUserInfo',
    async (_, { rejectWithValue }) => {
        try {
            const userInfo = await getUserInfo();
            return userInfo;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch user info');
        }
    }
);

// Async thunk to fetch user info by specific JWT token
export const fetchUserByJWT = createAsyncThunk(
    'auth/fetchUserByJWT',
    async (token, { rejectWithValue }) => {
        try {
            const userInfo = await fetchUserByToken(token);
            return userInfo;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch user by token');
        }
    }
);

// Async thunk to verify token
export const verifyUserToken = createAsyncThunk(
    'auth/verifyToken',
    async (token, { rejectWithValue }) => {
        try {
            const isValid = await verifyToken(token);
            return isValid;
        } catch (error) {
            return rejectWithValue(error.message || 'Token verification failed');
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState: {
        user: null,
        userId: null,
        userEmail: null,
        jwtToken: null,
        isLoggedIn: false,
        isLoading: false,
        error: null,
        tokenValid: null,
    },
    reducers: {
        setAuthData: (state, action) => {
            state.userId = action.payload.userId;
            state.userEmail = action.payload.userEmail;
            state.jwtToken = action.payload.jwtToken;
            state.isLoggedIn = action.payload.isLoggedIn;
        },
        setUser: (state, action) => {

            console.log('action.payload', action);
            state.user = action.payload;
            // Also set userId and userEmail from user data
            if (action.payload) {
                state.userId = action.payload.id;
                state.userEmail = action.payload.email;
            }
        },
        clearAuth: (state) => {
            state.user = null;
            state.userId = null;
            state.userEmail = null;
            state.jwtToken = null;
            state.isLoggedIn = false;
            state.tokenValid = null;
        },
        setToken: (state, action) => {
            state.jwtToken = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch User Info (with stored token)
            .addCase(fetchUserInfo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserInfo.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.error = null;
            })
            .addCase(fetchUserInfo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Fetch User By JWT Token
            .addCase(fetchUserByJWT.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserByJWT.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.error = null;
            })
            .addCase(fetchUserByJWT.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Verify Token
            .addCase(verifyUserToken.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(verifyUserToken.fulfilled, (state, action) => {
                state.isLoading = false;
                state.tokenValid = action.payload;
            })
            .addCase(verifyUserToken.rejected, (state, action) => {
                state.isLoading = false;
                state.tokenValid = false;
                state.error = action.payload;
            });
    },
});

export const { setAuthData, setUser, clearAuth, setToken } = authSlice.actions;
export default authSlice.reducer;

